import { useEffect, useRef, useState } from 'react';
import { cn } from './cn';

/*
  LiveValue — значение, которое показывает свою свежесть (DESIGN.md R25).
  Приём характера: цифра в системе никогда не «голая». Рядом с ней всегда видно,
  живой ли поток и сколько осталось до следующего опроса: тонкое кольцо добирается
  по кругу, новое значение мягко подкатывается снизу. Если поток встал — значение
  выцветает и само говорит, какого оно возраста. Пользователь знает, доверять ли цифре,
  не открывая ничего дополнительно.
*/

const RING_R = 4.5;
const RING_C = 2 * Math.PI * RING_R;

function FreshnessRing({ progress, live }: { progress: number; live: boolean }) {
  return (
    <svg width={12} height={12} viewBox="0 0 12 12" className="shrink-0 -rotate-90" aria-hidden>
      <circle
        cx={6}
        cy={6}
        r={RING_R}
        fill="none"
        stroke="var(--line-strong)"
        strokeWidth={1.5}
        strokeDasharray={live ? undefined : '2 2.5'}
        opacity={live ? 0.5 : 1}
      />
      {live && (
        <circle
          cx={6}
          cy={6}
          r={RING_R}
          fill="none"
          stroke="var(--success-fg)"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeDasharray={RING_C}
          strokeDashoffset={RING_C * (1 - progress)}
        />
      )}
    </svg>
  );
}

interface LiveValueProps {
  /** Готовая к показу строка значения. */
  value: string;
  unit?: string;
  /** Идёт ли поток данных. Нет — значение выцветает и показывает возраст. */
  live?: boolean;
  /** Период опроса: по нему добирается кольцо. */
  intervalMs?: number;
  /** Возраст данных, когда поток встал: «45 с назад». */
  ageLabel?: string;
  size?: 'md' | 'lg';
  /** Inline elapsed duration: inherited text, stable width, no polling ring or rolling digits. */
  variant?: 'metric' | 'elapsed';
  className?: string;
}

export function LiveValue({
  value,
  unit,
  live = true,
  intervalMs = 5000,
  ageLabel,
  size = 'md',
  variant = 'metric',
  className,
}: LiveValueProps) {
  const [progress, setProgress] = useState(0);
  const changedAt = useRef(0);

  useEffect(() => {
    changedAt.current = Date.now();
  }, [value]);

  useEffect(() => {
    if (!live || variant === 'elapsed') return;
    const id = window.setInterval(() => {
      setProgress(Math.min(1, (Date.now() - changedAt.current) / intervalMs));
    }, 120);
    return () => window.clearInterval(id);
  }, [live, intervalMs, variant]);

  if (variant === 'elapsed') {
    return (
      <span
        data-slot="live-value"
        data-variant="elapsed"
        className={cn('inline-block w-24 shrink-0 font-medium tabular-nums', className)}
      >
        {value}
      </span>
    );
  }

  return (
    <span data-slot="live-value" className={cn('inline-flex items-center gap-1.5', className)}>
      <span className="inline-flex items-baseline gap-1">
        <span
          key={value}
          className={cn(
            'inline-block animate-value-roll font-semibold tracking-tight tabular-nums',
            size === 'lg' ? 'text-3xl' : 'text-2xl',
            live ? 'text-fg' : 'text-fg-subtle',
          )}
        >
          {value}
        </span>
        {unit && <span className={cn('text-sm', live ? 'text-fg-muted' : 'text-fg-subtle')}>{unit}</span>}
      </span>
      <FreshnessRing progress={live ? progress : 0} live={live} />
      {!live && ageLabel && <span className="text-2xs text-fg-subtle">{ageLabel}</span>}
    </span>
  );
}
